<template>
  <div
    class="grid"
    :grid-type="title | toKebabCase"
    :style="isLoading ? 'overflow: hidden' : ''"
    v-show="!(items.length === 0 && isGlobalSearchActive === true)"
  >
    <GridSearch
      class="grid-search"
      v-bind="{ searchHeader, searchPlaceholder }"
      @searchSubmit="searchSubmit"
      :hidden="isGlobalSearchActive"
    />
    <slot></slot>
    <h3 v-if="isGlobalSearchActive">{{ title }}</h3>
    <LocalizationProvider language="en-EN">
      <IntlProvider locale="en">
        <KendoUiGrid
          :columns="dataColumns | formatTitle | formatSelectedColumns(selectedColumns)"
          :column-menu="true"
          :data-items="result | formatDate(columns)"
          :filter="dataFilter"
          :pageable="isPageable ? pageable : false"
          :page-size="pageSize"
          :scrollable="'scrollable'"
          :skip="skip"
          :sort="dataSort"
          :sortable="true"
          :take="take"
          :total="total"
          @filterchange="filterChange"
          @pagechange="pageChange"
          @sortchange="sortChange"
        >
          <KendoGridToolbar>
            <div class="toolbar-button-group">
              <span title="Add new" class="k-button k-primary toolbar-button" @click="addClick">
                <span class="icon icon-add"></span>
              </span>
              <span title="Export Excel" class="k-button k-primary toolbar-button" @click="exportExcel">
                <span class="icon icon-excel"></span>
              </span>
              <span title="Clear All Filters" class="k-button k-primary toolbar-button" @click="clearAllFilters">
                Clear Filters
              </span>
            </div>
            <div class="toolbar-button-group">
              <span
                title="Select All Columns"
                :class="`k-button toolbar-button ${allColumnsSelected ? 'toolbar-button-disabled' : 'k-primary'}`"
                @click="selectAllColumns"
              >
                {{ hasLargeViewOption ? 'Full View' : 'Select All' }}
              </span>
              <span
                v-if="hasLargeViewOption"
                title="Large View Default Columns"
                :class="`k-button toolbar-button ${largeViewColumnsSelected ? 'toolbar-button-disabled' : 'k-primary'}`"
                @click="restoreLargeViewColumns"
              >
                Large View
              </span>
              <span
                title="Restore Default Columns"
                :class="`k-button toolbar-button ${defaultColumnsSelected ? 'toolbar-button-disabled' : 'k-primary'}`"
                @click="restoreDefaultColumns"
              >
                {{ hasLargeViewOption ? 'Quick View' : 'Restore' }}
              </span>
              <div class="toolbar-button">
                <multiselect
                  v-model="selectedColumns"
                  track-by="field"
                  label="title"
                  :allowEmpty="false"
                  :options="dataColumns | removeEdit | formatTitle"
                  :clear-on-select="false"
                  :close-on-select="false"
                  :multiple="true"
                  :show-labels="false"
                  @remove="deselectOption"
                  @select="selectOption"
                >
                  <span slot="selection" slot-scope="{ values, isOpen }">
                    <span v-if="values.length && !isOpen">{{ values.length }} options selected</span>
                    <span v-else></span>
                  </span>
                  <span slot="option" slot-scope="{ option }">
                    <input :id="option.title" type="checkbox" v-model="option.selected" />
                    {{ option.title }}
                  </span>
                </multiselect>
              </div>
            </div>
          </KendoGridToolbar>
          <template #buttons="{ props: { dataItem } }">
            <td>
              <div class="buttons">
                <EditCell @edit="editClick(dataItem)" />
                <button v-if="isSampleGrid" class="k-button k-primary" @click="openSampleReport(dataItem)">
                  <i class="icon icon-print"></i>
                </button>
                <button v-if="isSampleGrid" class="k-button k-primary" @click="openSampleGraph(dataItem)">
                  <i class="icon icon-bar_chart"></i>
                </button>
              </div>
            </td>
          </template>
          <template #logoPath="{ props: { dataItem } }">
            <td class="image">
              <img :src="dataItem.logoPath" :alt="dataItem.code" />
            </td>
          </template>
          <template #unit="{ props: { dataItem } }">
            <td>
              <a class="grid-link" href="#" @click="openUnitByName(dataItem)">{{ dataItem.unitName }}</a>
            </td>
          </template>
          <template #customerName="{ props: { dataItem } }">
            <td>
              <a class="grid-link" href="#" @click="openCustomerByName(dataItem)">{{ dataItem.customerName }}</a>
            </td>
          </template>
          <template #severity="{ props: { field, dataItem } }">
            <td :class="getRelatedSeverityClass(field, dataItem.sampleNumber)" :key="update">
              <!-- This is not the best spot to put this, but there isn't a way to use two cell templates on a cell. -->
              {{ getDisplayValue(field, dataItem) }}
            </td>
          </template>
        </KendoUiGrid>
      </IntlProvider>
    </LocalizationProvider>
    <div v-if="isLoading && !isGlobalSearchActive" class="loading-overlay">
      <div class="loading-wrapper">
        <RingLoader class="loading-spinner" />
      </div>
    </div>
  </div>
</template>

<script>
// Kendo
import {
  Grid as KendoUiGrid,
  GridToolbar as KendoGridToolbar,
  filterGroupByField,
  sortGroupByField
} from '@progress/kendo-vue-grid';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-vue-intl';
import { filterBy, orderBy } from '@progress/kendo-data-query';
import { formatDate, parseDate } from '@telerik/kendo-intl';

// Miscellaneous
import debounce from 'lodash.debounce';
import cloneDeep from 'lodash.clonedeep';
import RingLoader from '../shared/RingLoader.vue';
import Multiselect from 'vue-multiselect';

// Services
import GridDataService from 'services/grid-data.service';
import QueryStringService from 'services/query-string.service';
import StringHelperService from 'services/string-helper.service';
import Constants from '../../constants/constants';
import KendoConstants from '../../../constants/kendo.constants';
import EditCell from './EditCell.vue';
import GridSearch from './GridSearch.vue';
import EventBus from '../../../event-bus';
import RoundingMixin from './RoundingMixin';

export default {
  name: 'Grid',
  data() {
    return {
      // The filter and sort property is set to the component data to prevent mutating the property.
      dataColumns: this.columns,
      dataFilter: this.filter,
      dataSort: this.sort,
      data: {},
      firstLoad: true,
      loadingTimer: null,
      hasLargeViewOption: false,
      update: 0,
      cookieColumnsName: this.getCookieName('columns'),
      cookiePageSizeName: this.getCookieName('page-size'),
      ...cloneDeep(Constants.Grid.default)
    };
  },
  filters: {
    formatDate(dataItems, columns) {
      dataItems.forEach(dataItem => {
        Object.keys(dataItem).forEach(key => {
          const column = columns.find(column => column.field === key);

          if (!dataItem[key] || !column?.filter) {
            return;
          }

          if (column.filter === Constants.Kendo.filter.date) {
            dataItem[key] = formatDate(parseDate(dataItem[key]), 'd');
          }
        });
      });

      return dataItems;
    },

    formatSelectedColumns(columns, selectedColumns) {
      return columns.map(({ field, ...otherProperties }) => {
        const isSelected = selectedColumns.some(selectedColumn => selectedColumn.field === field);

        return {
          field,
          hidden: !isSelected,
          ...otherProperties
        };
      });
    },

    removeEdit(columns) {
      return columns.filter(dataItem => dataItem.field !== '');
    },

    formatTitle(columns) {
      return columns.map(({ field, title = StringHelperService.fieldToTitle(field), ...otherProperties }) => {
        return {
          field,
          title,
          ...otherProperties
        };
      });
    },

    toKebabCase: StringHelperService.toKebabCase
  },
  props: {
    api: {
      type: Object,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    doClientSide: {
      type: Boolean,
      required: true
    },
    filter: Object,
    paging: Object,
    searchHeader: {
      type: String,
      required: true
    },
    searchPlaceholder: {
      type: String,
      required: true
    },
    sort: Array,
    title: {
      type: String,
      required: true
    }
  },
  components: {
    GridSearch,
    KendoGridToolbar,
    KendoUiGrid,
    Multiselect,
    RingLoader,
    EditCell,
    LocalizationProvider,
    IntlProvider
  },
  computed: {
    allColumnsSelected() {
      return this.dataColumns.every(({ selected }) => selected);
    },

    defaultColumnsSelected() {
      return this.selectedColumnsMatch(this.defaultColumns);
    },

    largeViewColumnsSelected() {
      return this.selectedColumnsMatch(this.defaultLargeViewColumns);
    },

    filterItems() {
      if (this.doClientSide) {
        return filterBy({ ...this.items }, this.dataFilter);
      }

      return this.items;
    },

    isSampleGrid() {
      return this.title === Constants.Grid.OilSample.title || this.title === Constants.Grid.CoolantSample.title;
    },

    isPageable() {
      return this.serverTotal > this.isPageableThreshold;
    },

    result() {
      if (this.doClientSide) {
        return this.sortItems.slice(this.skip, this.take + this.skip);
      }

      return this.items;
    },

    sortItems() {
      if (this.doClientSide) {
        return orderBy(this.filterItems, this.dataSort);
      }

      return this.items;
    },

    total() {
      if (this.doClientSide) {
        return this.items ? this.items.length : 0;
      }

      return this.serverTotal;
    }
  },
  methods: {
    saveCookie(name, value) {
      this.$cookies.set(name, value, this.defaultPersistanceTime);
    },
    getCookieName(partialName) {
      const cleanTitle = `${window.location.pathname}-${this.title}`.toLowerCase().replace(/[^a-z]/g, '-');
      return `save-${cleanTitle}-${partialName}`.replace(/--/g, '-');
    },
    clearAllFilters() {
      window.history.replaceState(null, null, window.location.pathname);

      this.dataFilter = { filters: [] };
      this.params.filter = {};
      this.dataSort = [];
      this.params.sort = {};

      this.checkActiveColumns();
    },

    selectedColumnsMatch(columnsToMatch) {
      return (
        this.selectedColumns.every(({ field }) => {
          return columnsToMatch.some(colToMatch => colToMatch.field === field);
        }) && this.selectedColumns.length === columnsToMatch.length
      );
    },

    setSelectedColumns(columns) {
      this.selectedColumns = columns;
      this.dataColumns = this.dataColumns
        .map(({ field, selected, ...otherProperties }) => {
          const isSelected = this.selectedColumns.some(selectedColumn => selectedColumn.field === field);

          return {
            field,
            selected: isSelected,
            ...otherProperties
          };
        })
        .filter(Boolean);
      this.persistSelectedColumns();
    },

    loadDefaultPageSize() {
      const pageSize = parseInt(this.$cookies.get(this.cookiePageSizeName));
      if (pageSize) {
        this.take = pageSize;
      }
    },

    persistPageSize() {
      this.saveCookie(this.cookiePageSizeName, this.take);
    },

    loadDefaultColumns() {
      // check cookies... if there is a cookie, use that
      let savedCols = this.getPersistedColumns();
      const persistedInfo = this.findMatchingDataColumns(savedCols);

      if (persistedInfo) {
        this.dataColumns = persistedInfo.dataColumns;
        this.selectedColumns = persistedInfo.selectedColumns;
      } else {
        this.selectedColumns = this.defaultColumns;
      }
    },

    persistSelectedColumns() {
      var selectedCols = this.dataColumns.filter(i => i.selected).map(i => i.field);
      this.saveCookie(this.cookieColumnsName, JSON.stringify(selectedCols));
    },

    getPersistedColumns() {
      let savedCols = this.$cookies.get(this.cookieColumnsName);
      if (!savedCols) {
        return null;
      }
      try {
        savedCols = JSON.parse(savedCols);
      } catch {
        return null;
      }

      return savedCols;
    },

    findMatchingDataColumns(cols) {
      if (!cols || cols.length == 0) {
        return null;
      }

      const dataColumns = this.dataColumns.map(({ field, ...otherProperties }) => {
        return {
          field,
          ...otherProperties,
          selected: cols.some(col => col === field)
        };
      });

      const selectedColumns = dataColumns
        .map(({ selected, ...otherProperties }) => {
          if (selected) {
            return {
              ...otherProperties,
              selected: true
            };
          }
        })
        .filter(Boolean);

      return { dataColumns, selectedColumns };
    },

    checkActiveColumns() {
      this.dataColumns.forEach(column => {
        if (!column.field) return;

        const isFilterActive = filterGroupByField(column.field, this.dataFilter);
        const isSortActive = sortGroupByField(column.field, this.dataSort);

        column.headerClassName = isFilterActive || isSortActive ? 'active' : '';
      });
    },

    addTitleAttributeToColumnHeaders() {
      document.querySelectorAll('.k-grid-header .k-header .k-link').forEach(element => {
        if (element.textContent) {
          element.setAttribute('title', element.textContent);
        }
      });
    },

    createFilterEventListenersToAddStepAttribute() {
      document.querySelectorAll('th.k-header').forEach(header => {
        const title = header.querySelector('.k-link')?.getAttribute('title');

        if (!title) {
          return;
        }

        const isFilterableByFloat = this.columns.find(column => {
          return column?.title === title || StringHelperService.fieldToTitle(column.field) === title;
        })?.isFilterableByFloat;

        const moreVertical = header.querySelector('.k-i-more-vertical');

        if (!moreVertical) {
          return;
        }

        moreVertical.parentNode.addEventListener('click', event => {
          setTimeout(() => {
            const filterMenuItem = document.querySelector(
              '.k-columnmenu-item-wrapper .k-columnmenu-item:has(.k-icon.k-i-filter)'
            );

            if (filterMenuItem) {
              const filterMenuActions = document.querySelector('.k-columnmenu-actions');
              if (filterMenuActions) {
                return;
              }

              filterMenuItem.click();

              if (isFilterableByFloat) {
                setTimeout(() => {
                  document.querySelectorAll('.k-filter-menu-container input[type="number"]').forEach(input => {
                    input.setAttribute('step', 0.01);
                  });
                }, 550);
              }
            } else {
              // Fallback approach: try to find by text content
              const menuItems = document.querySelectorAll('.k-columnmenu-item');
              const filterMenuItemByText = Array.from(menuItems).find(item => item.textContent.trim() === 'Filter');

              if (filterMenuItemByText) {
                filterMenuItemByText.click();
              }
            }
          }, 500);
        });
      });
    },
    deselectOption(option) {
      this.dataColumns = this.dataColumns.map(({ field, selected, ...otherProperties }) => {
        return {
          field,
          selected: field === option.field ? false : selected,
          ...otherProperties
        };
      });
      this.persistSelectedColumns();
    },

    exportExcel() {
      const exportPagination = { ...this.params.pagination };

      const dateFilters = this.columns.filter(({ filter }) => filter === KendoConstants.filter.date);

      const filtersWithFormattedDates = Object.fromEntries(
        Object.entries(this.params.filter).map(([key, value]) => {
          return dateFilters.some(dateFilter => key.startsWith(dateFilter.field) && key.endsWith('query'))
            ? [key, new Date(value).toISOString()]
            : [key, value];
        })
      );

      const urlParams = new URLSearchParams(window.location.search);
      const unitIdQuery = urlParams.get('unitIdQuery');

      if (unitIdQuery) {
        this.params.globalSearch.unitIdQuery = unitIdQuery;
      }

      const searchParams = new URLSearchParams({
        ...filtersWithFormattedDates,
        ...this.params.sort,
        ...exportPagination,
        ...this.params.globalSearch
      });

      location.assign(`${this.api.export}?${searchParams.toString()}`);
    },

    getDisplayValue(field, dataItem) {
      const column = this.columns.find(column => column.field === field);

      return column?.display ? dataItem[column.display] : dataItem[field];
    },

    getRelatedSeverityClass(field, sampleNumber) {
      if (field === Constants.ColumnName.OilSample.particleCount25.field) {
        field += 'To50';
      }

      let severityField = `${field}Severity`;

      if (field === Constants.ColumnName.OilSample.sampleSeverityName.field) {
        severityField = 'sampleSeverityName';
      }

      if (
        field === Constants.ColumnName.OilSample.waterPercent.field ||
        field === Constants.ColumnName.OilSample.waterPpm.field
      ) {
        severityField = 'waterSeverity';

        const waterField = this.data.find(sample => sample.sampleNumber === sampleNumber)?.[field];
        if (waterField === null || waterField === undefined) {
          return `severity-id-normal`;
        }
      }

      field = field.replace(/display/i, '');

      const severity = this.data.find(sample => sample.sampleNumber === sampleNumber)?.[severityField]?.toLowerCase();

      return `severity-id-${severity || 'normal'}`;
    },

    loadData(refreshSearch = 0, sendToFirstPage = true) {
      if (this.isRequesting) {
        // we are already in the middle of a request
        return;
      }
      this.isRequesting = true;

      this.setLoading(true);

      if (this.firstLoad || sendToFirstPage) {
        this.skip = 0;
        this.params.pagination = QueryStringService.getPaginationSearchParams(this.take, 0);
      }
      this.firstLoad = false;

      let combinedFilters = { ...this.params.filter };

      if (window.location.search.length) {
        const queryString = new URLSearchParams(window.location.search);
        combinedFilters = {
          ...combinedFilters,
          ...Object.fromEntries(queryString.entries())
        };
      }

      GridDataService.getGridData(
        {
          apiUrl: this.api.data,
          params: {
            ...combinedFilters,
            ...this.params.sort,
            ...this.params.pagination,
            ...this.params.globalSearch,
            refreshSearch
          }
        },
        [...this.dataColumns.map(({ field }) => field), ...this.dataColumns.map(({ display }) => display)]
      ).then(async ({ kendoData, data, headers }) => {
        this.isRequesting = false;

        if (!(await kendoData)) {
          this.setLoading(false);
          return;
        }

        const xPagination = JSON.parse(await headers[Constants.Api.xPagination]);
        const decimalCols = this.dataColumns.filter(item => item.roundDecimals !== undefined);
        this.pageSize = xPagination.pageSize;
        this.serverTotal = xPagination.totalCount;
        this.data = this.formatColumnData(decimalCols, data);
        this.items = this.formatColumnData(decimalCols, await kendoData);
        this.setLoading(false);
      });
    },

    resetLoading() {
      // TODO: Fix this, but for now, I have to init this event to show/hide the individual grid "Search" on the dashboard. There is a better way, but for now, going with it
      EventBus.$emit('isLoading', true);
      EventBus.$emit('isLoading', false);
    },

    setLoading(bool) {
      if (bool) {
        this.loadingTimer = setTimeout(() => {
          this.isLoading = bool;
          EventBus.$emit('isLoading', bool);
        }, KendoConstants.spinner.timeout);
      } else {
        clearTimeout(this.loadingTimer);
        this.isLoading = bool;
        EventBus.$emit('isLoading', bool);
      }
    },

    formatColumnData(decimalCols, data) {
      if (decimalCols !== undefined && decimalCols.length) {
        decimalCols.forEach(({ decimalPlaces, roundDecimals, field }) => {
          data.map(dataItem => {
            dataItem[field] = this.roundVal(decimalPlaces, roundDecimals, dataItem[field]);
            return dataItem;
          });
        });
      }
      return data;
    },

    selectAllColumns() {
      this.setSelectedColumns(this.$options.filters.formatTitle(this.dataColumns));
    },

    restoreDefaultColumns() {
      this.setSelectedColumns(this.defaultColumns);
    },

    restoreLargeViewColumns() {
      this.setSelectedColumns(this.defaultLargeViewColumns);
    },

    openUnitByName({ unitName }) {
      const url = Constants.Api.Endpoint.Unit.route;

      window.location = `${url}?unitNameOperator=eq&unitNameQuery=${unitName}`;
    },

    openCustomerByName({ customerName }) {
      const url = Constants.Api.Endpoint.Customer.route;

      window.location = `${url}?customerNameOperator=eq&customerNameQuery=${customerName}`;
    },

    openSampleGraph({ sampleNumber }) {
      let url;
      let id;

      if (this.title === Constants.Grid.OilSample.title) {
        url = Constants.Api.Endpoint.OilSample.graphRoute;
        id = this.data.find(sample => sample.sampleNumber === sampleNumber).oilSampleID;
      }

      if (this.title === Constants.Grid.CoolantSample.title) {
        url = Constants.Api.Endpoint.CoolantSample.graphRoute;
        id = this.data.find(sample => sample.sampleNumber === sampleNumber).coolantSampleID;
      }

      window.open(`${url}?id=${id}`, '_blank');
    },

    openSampleReport({ sampleNumber }) {
      let url;
      let id;

      if (this.title === Constants.Grid.OilSample.title) {
        url = Constants.Api.Endpoint.OilSample.reportRoute;
        id = this.data.find(sample => sample.sampleNumber === sampleNumber).oilSampleID;
      }

      if (this.title === Constants.Grid.CoolantSample.title) {
        url = Constants.Api.Endpoint.CoolantSample.reportRoute;
        id = this.data.find(sample => sample.sampleNumber === sampleNumber).coolantSampleID;
      }

      window.open(`${url}?id=${id}`, '_blank');
    },

    addClick() {
      this.$emit('add-button-clicked');
    },

    selectOption(option) {
      this.dataColumns = this.dataColumns.map(({ field, selected, ...otherProperties }) => {
        return {
          field,
          selected: field === option.field ? true : selected,
          ...otherProperties
        };
      });
      this.persistSelectedColumns();
    },

    editClick(e) {
      this.$emit('edit-button-clicked', e, this.data);
    }
  },
  created() {
    EventBus.$on(Constants.EventBus.globalSearchSubmit, (query, isActive) => {
      this.searchSubmit(query, isActive);
    });

    EventBus.$on(Constants.EventBus.isGlobalSearchActive, bool => {
      this.isGlobalSearchActive = bool;
    });

    EventBus.$on(Constants.EventBus.globalClearAllFilters, () => {
      this.clearAllFilters();
    });

    // These methods are created here to add debounce to the functions.
    this.filterChange = debounce(({ filter, event }) => {
      this.dataFilter = filter;
      const isFilterActive = filterGroupByField(event.field, filter);
      const isSortActive = sortGroupByField(event.field, this.dataSort);
      const changedColumn = this.dataColumns.find(({ field }) => field === event.field);

      if (changedColumn) {
        changedColumn.headerClassName = isFilterActive || isSortActive ? 'active' : '';
      }

      if (this.doClientSide) {
        return;
      }

      if (!filter) {
        this.params.filter = {};

        return;
      }

      this.params.filter = QueryStringService.getFilterSearchParams(filter.filters);
    }, Constants.Api.debounce.filter);

    this.pageChange = debounce(({ page }) => {
      if (this.doClientSide) {
        this.skip = page.skip;
        this.take = page.take;
        return;
      }

      this.skip = page.skip;
      this.take = page.take;
      this.params.pagination = QueryStringService.getPaginationSearchParams(page.take, page.skip);
    }, Constants.Api.debounce.pagination);

    this.searchSubmit = debounce((query, isActive) => {
      this.params.globalSearch = QueryStringService.getGlobalSearchSearchParams(query, isActive);
    }, Constants.Api.debounce.globalSearch);

    this.sortChange = debounce(({ sort, event }) => {
      const isSortActive = sortGroupByField(event.field, sort);
      const isFilterActive = filterGroupByField(event.field, this.dataFilter);
      const changedColumn = this.dataColumns.find(({ field }) => {
        return field === event.field;
      });

      if (changedColumn) {
        changedColumn.headerClassName = isSortActive || isFilterActive ? 'active' : '';
      }

      if (this.doClientSide) {
        this.dataSort = sort;

        return;
      }

      if (!sort) {
        this.dataSort = [];
        this.params.sort = {};

        return;
      }

      this.dataSort = sort;
      this.params.sort = QueryStringService.getSortSearchParams(sort);
    }, Constants.Api.debounce.sort);
  },
  mounted() {
    if (this.sort.length) this.params.sort = QueryStringService.getSortSearchParams(this.sort);

    this.resetLoading();

    this.$nextTick(() => {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has('unitID')) {
        this.dataFilter.filters.push({
          logic: 'and',
          filters: [
            {
              field: 'unitID',
              operator: 'eq',
              value: searchParams.get('unitID')
            }
          ]
        });
      }
      if (searchParams.has('sampleSeverityName')) {
        this.dataFilter.filters.push({
          logic: 'and',
          filters: [
            {
              field: 'sampleSeverityName',
              operator: 'eq',
              value: searchParams.get('sampleSeverityName')
            }
          ]
        });
      }

      this.params.filter = QueryStringService.getFilterSearchParams(this.dataFilter.filters);
      this.checkActiveColumns();

      this.loadData();
    });

    this.defaultColumns = this.dataColumns
      .map(({ selected, ...otherProperties }) => {
        if (selected) {
          return {
            selected,
            ...otherProperties
          };
        }
      })
      .filter(Boolean);

    this.defaultLargeViewColumns = this.dataColumns
      .map(({ selectedLargeView, ...otherProperties }) => {
        if (selectedLargeView) {
          return {
            selectedLargeView,
            ...otherProperties
          };
        }
      })
      .filter(Boolean);
    this.hasLargeViewOption = !!this.defaultLargeViewColumns.length;

    this.loadDefaultColumns();
    this.loadDefaultPageSize();
  },
  updated() {
    this.addTitleAttributeToColumnHeaders();
    this.createFilterEventListenersToAddStepAttribute();
  },
  mixins: [RoundingMixin],
  watch: {
    'params.filter': {
      deep: true,
      handler() {
        this.loadData();
      }
    },
    'params.sort': {
      deep: true,
      handler() {
        this.loadData(0, false);
      }
    },
    'params.pagination': {
      deep: true,
      handler(info) {
        this.loadData(0, false);
        this.persistPageSize();
      }
    },
    'params.globalSearch': {
      deep: true,
      handler() {
        this.loadData();
      }
    },
    selectedColumns() {
      // Updates grid elements with severity.
      this.update += 1;
    }
  }
};
</script>

<style lang="scss" scoped>
@import url('../../../../../../node_modules/vue-multiselect/dist/vue-multiselect.min.css');

.grid {
  flex-direction: column;
  height: calc(100vh - 50px);
  min-width: 100%;
  padding: 12px;
  width: auto;

  .buttons {
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 0 3px;

    button {
      margin-right: 5px;

      &:last-child {
        margin-right: 0;
      }
    }
  }

  .severity-id {
    &-reportable {
      background: nth($severity-color-reportable, 2);
    }

    &-abnormal {
      background: nth($severity-color-abnormal, 2);
    }

    &-critical {
      background: nth($severity-color-critical, 2);
    }
  }

  .grid-link {
    color: inherit;
    font-size: inherit;
    text-decoration: underline;
  }

  .navigation-checkbox:checked > & {
    min-width: calc(100% - 250px);
  }

  .image {
    text-align: center;
  }

  :deep(.k-grid) {
    height: auto !important;
    max-width: calc(100vw - 40px);

    table {
      width: 100% !important;
    }

    tr {
      td {
        padding: 7px 8px 5px 8px;
        vertical-align: top;

        &:first-child {
          padding: 4px 8px;
        }
      }
    }

    th.k-header.active > div > div {
      background-color: $accent;
      color: $white;
    }

    .k-header.k-grid-toolbar::after {
      content: none !important;
    }

    .k-grid-content.k-virtual-content {
      max-height: 65vh;
      max-width: calc(100vw - 40px);
    }

    thead .k-header {
      position: relative;

      > .k-link {
        margin-right: 1em;
      }
    }
  }

  input[type='checkbox'] {
    margin-right: 4px;
  }

  .k-grid-toolbar {
    display: flex;
    padding-right: 4px;
    z-index: 1;

    .toolbar-button-group {
      align-items: center;
      display: flex;

      &:last-child {
        margin-left: auto;
      }

      .toolbar-button {
        margin-right: 4px;

        &-disabled {
          pointer-events: none;
        }

        .icon {
          // Taken from Kendo's all.css to match button height.
          line-height: 1.4285714286;
        }
      }
    }
  }
}

.performanceMessage {
  color: $gray;
  margin-top: 5px;
}

.loading-overlay {
  background: rgba(0, 0, 0, 0.5);
  bottom: 0;
  height: 100vh;
  position: fixed;
  right: 0;
  top: 0;
  width: calc(100% - 250px);
  z-index: 10000;

  .loading-wrapper {
    height: 100%;
    position: relative;
    width: 100%;

    .loading-spinner {
      left: 50%;
      margin-right: -50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);

      :deep(div) {
        // Overrides default value from the loader component.
        border-color: $white-offset transparent transparent !important;
      }
    }
  }
}
</style>

<style lang="scss">
// Removing the second set of filters and logic selector from the column menu.
.k-filter-menu-container {
  .k-filter-and,
  .k-filter-or {
    display: none !important;

    + div .k-textbox {
      display: none !important;
    }
  }
}
</style>
